/* eslint-disable class-methods-use-this */
/* eslint-disable max-lines */
import { kitchenLayouts } from '../blueprint-classes/kitchens/data';
import { newEmptyIsland } from '../helpers';

import { Blueprint, BLUEPRINT_SCHEMA_VERSION, BlueprintData, Island, Kitchen, KitchenDataOut, PanelItem, PaymentStatus } from '../blueprint-classes';
import { Component, Components } from '../component-classes';
import { decodeIslandSku, getRawLayoutOrientationMap, parseRawIslands, setKitchenConnections } from './functions';
import { RawAppliance, RawBlueprint, RawIsland } from './types';

export class ProcessRawBlueprint {
	#blueprint: Blueprint;
	#kitchen: Kitchen;
	#components: Components;
	#defaultIsland: RawIsland | null;
	#defaultIslandId: string | null;

	constructor (blueprint: RawBlueprint) {
		try {
			// console.log('schema:process-raw-blueprint:raw', blueprint);
			this.#defaultIsland = null;
			this.#defaultIslandId = null;
			this.#components = new Components({ userEmail: blueprint.email });
			const kitchenLayout = (blueprint.layout || '').replace(/\\/gu, '').replace(/"/gu, '');
			let layout: string | null = null;

			switch (kitchenLayout.substring(0, 2).toLowerCase()) {
				case 'li':
				case 'si': layout = kitchenLayouts.Linear; break;
				case 'u-': layout = kitchenLayouts.UShaped; break;
				case 'l-': layout = kitchenLayouts.LShaped; break;
				case 'ga': layout = kitchenLayouts.Galley; break;
				default: layout = kitchenLayouts.Custom;
			}

			this.#blueprint = new Blueprint({ userEmail: 'SYSTEM',
				dataIn: {
					submissionDate: blueprint.date,
					submissionId: blueprint.date,
					email: blueprint.email,
					schemaVersion: BLUEPRINT_SCHEMA_VERSION,
					thumbnail: blueprint.image,
					kitchens: {
						children: [
							{
								baseVersion: null,
								version: 1,
								description: kitchenLayout,
								kitchenLayout,
								layout
							}
						]
					},
					metadata: {
						estimateNumber: blueprint.estimateNumber,
						invoiceNumber: blueprint.invoiceNumber,
						workorderNumber: blueprint.workorderNumber,
						paymentStatus: PaymentStatus.NotPaid
					}
				}});


			const rawIslands = parseRawIslands(blueprint.islands);
			// console.log('schema:rawIslands', JSON.stringify(rawIslands, null, '\t'));

			// eslint-disable-next-line prefer-destructuring
			this.#kitchen = this.#blueprint.kitchens.children[0];
			this.#kitchen.material.setSurround(blueprint.surround.replace(/ /gu, '').toLowerCase());
			this.#kitchen.material.setCountertop(blueprint.counter.replace(/ /gu, '').toLowerCase());
			this.#kitchen.fuelchoice = this.getFuelchoice(blueprint.fuelchoice);

			this.processRawIslands(rawIslands);

			if (this.#defaultIslandId) this.#kitchen.design.islands.setDefaultIsland(this.#defaultIslandId)

			setKitchenConnections(this.#kitchen.design.islands);

			// console.log('schema:process-raw-blueprint:processed', this.blueprintData);
		} catch (e) {
			console.warn(e);
			throw e;
		}
	}

	// ----------------------------------------
	// Public Getters / Setters
	// ----------------------------------------
	get blueprint (): Blueprint {
		// console.log('schema:processRawBlueprint:blueprint', this.#blueprint.serialize());
		return this.#blueprint;
	}

	get blueprintData (): BlueprintData {
		return this.#blueprint.serialize();
	}

	// ----------------------------------------
	// Public Methods
	// ----------------------------------------

	// Returns the latest version. Render initial data if no versions exist
	getRenderingData (): KitchenDataOut {
		return this.#kitchen.serialize();
	}

	// ---------------------------------------------
	// Private Methods - Processing Raw Blueprint
	// ---------------------------------------------

	// Called by the constructor to parse the raw json islands data
	// -- sorts islands base on orientation extracted by layout type
	private processRawIslands (rawIslands: Array<RawIsland>): void {
		const islandOrientations = getRawLayoutOrientationMap(this.#kitchen.kitchenLayout);
		const islandObject = rawIslands;
		let sorted: RawIsland[] = [];

		islandObject.forEach((island, index) => {
			// console.log('schema:island:appliances', island.appliances);
			island.appliances = island.appliances.filter((appliance) => {
				const skuData = this.#components.findSku(appliance.sku) ?? null;
				const islandSku = appliance.sku.indexOf('RTAC-K3') !== -1 || appliance.sku.indexOf('RTAP-P3') !== -1;

				return islandSku || skuData !== null;
			});
			// console.log('schema:island:appliances', island.appliances);

			island.orientation = islandOrientations === null ? 0 : islandOrientations[index] || 0;
			island.isCornerIsland = Math.abs(island.orientation) === 45;

			if (island.orientation >= 0) {
				// island.orientation = island.orientation === 0 || island.orientation === 360 ? 0 : 1;
				sorted.push(island);
			} else {
				// island.orientation = island.orientation === -360 ? 0 : -1;
				sorted = [island, ...sorted];
			}

			// first island @ orientation 0 (front view) is labeled default
			if (island.orientation === 0 && this.#defaultIsland === null) this.#defaultIsland = island;
		});
		// console.log('schema:defaultIsland', this.#defaultIsland);

		// no defaultIsland means we have a galley
		if (this.#defaultIsland === null) this.#defaultIsland = islandObject.find((fIsland) => fIsland.orientation === -1) ?? null;

		// console.log('schema:processRawIslands:sorted', sorted);
		this.preprocessRawIslandsForNewIslands(sorted);
	}

	private preprocessRawIslandsForNewIslands (rawIslands: Array<RawIsland>): void {
		const newRawIslands: Array<Partial<RawIsland>> = [];

		rawIslands.forEach((rawIsland, islandIndexPosition) => {
			const newPrevIsland: Partial<RawIsland> = { appliances: [], orientation: rawIsland.orientation, sku: '' };
			const newNextIsland: Partial<RawIsland> = { appliances: [], orientation: rawIsland.orientation, sku: '' };

			let islandToTheLeft = islandIndexPosition > 0 && rawIslands[islandIndexPosition - 1].orientation === (rawIsland.orientation || 0) - 90;
			let islandToTheRight = islandIndexPosition < rawIslands.length - 1 && rawIslands[islandIndexPosition + 1].orientation === (rawIsland.orientation || 0) + 90;

			// console.log('schema:sortAppliances (raw)', rawIsland.appliances.sort((a: RawAppliance, b: RawAppliance) => a.xPosition - b.xPosition));
			rawIsland.appliances.sort((a: RawAppliance, b: RawAppliance) => a.xPosition - b.xPosition).forEach((appliance: RawAppliance, applianceIndex) => {
				// make the assumption that the new island goes to the right if the xPosition > 1
				const position = (islandToTheLeft && islandToTheRight)
					? 0
					: appliance.xPosition > 1
						? 1
						: islandToTheLeft
							? 1
							: 0;

				console.log('schema:appliance.sku', appliance.sku);
				if (appliance.sku.indexOf('RTAC-K3') !== -1 || appliance.sku.indexOf('RTAP-P3') !== -1) {
					console.info(`schema:Found new add-on (${appliance.sku}), making it an island at position ${position}, orientation ${rawIsland.orientation}`);

					if (position === 0) {
						newPrevIsland.sku = appliance.sku;
						islandToTheLeft = true;
					} else {
						newNextIsland.sku = appliance.sku;
						islandToTheRight = true;
					}

					delete rawIsland.appliances[applianceIndex];
				}

				if (['C1CHCS', 'FX4P-LRAM-T', 'FX5MIN-LRAM-T'].includes(appliance.sku)) {
					console.info(`schema:Found appliance (${appliance.sku}) for first new add-on Island. Adding it to the island at position ${position}.`);
					appliance.sku = appliance.sku === 'FX5MIN-LRAM-T' ? 'FX4P-LRAM-T' : appliance.sku;
					const tempIsland = position === 0 ? newPrevIsland : newNextIsland;
					tempIsland?.appliances.push(appliance);
					tempIsland.sku = 'RTAC-K3';

					if (appliance.sku === 'FX4P-LRAM-T') {
						const logSku = this.#components.findSku(appliance.sku);

						tempIsland?.appliances.push({
							sku: 'LOG-INSERT',
							height: logSku.size.height,
							width: logSku.size.width,
							xPosition: appliance.xPosition
						});
					}
					delete rawIsland.appliances[applianceIndex];
				}
			});

			if (newPrevIsland.sku) { newRawIslands.push(newPrevIsland); }
			newRawIslands.push(rawIsland);
			// newRawIslands.push(this.getCleanIsland(rawIsland));
			if (newNextIsland.sku) { newRawIslands.push(newNextIsland); }
		});

		// console.log('schema:preprocessIslandsForNewIslands:->', deepCopy(newIslands));
		newRawIslands.forEach((rawIsland) => {
			// const prevRawIsland = i === 0 ? null : newRawIslands[i - 1];
			const decodedSku = decodeIslandSku(rawIsland.sku ?? '');
			if (decodedSku) {
				const island = newEmptyIsland(this.#kitchen, decodedSku.islandSubType, rawIsland.orientation ?? 0, rawIsland === this.#defaultIsland);
				this.groupRawAppliances(island as Island, rawIsland);

				if (rawIsland === this.#defaultIsland) this.#defaultIslandId = island.id;
			}
		});

		// console.log('schema:preprocessRawIslandsForNewIslands:->', newRawIslands, islands.children);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	// private groupRawAppliances (appliances: Array<RawAppliance>): Array<any> {
	private groupRawAppliances (island: Island, rawIsland: Partial<RawIsland>): void {
		const frontPanel = island.panels.getFrontPanel();
		const appliances = rawIsland.appliances?.filter((a) => a) ?? [];
		const { panelItemGroups } = frontPanel;
		// const panelItemGroup = island.panels.getFrontPanel().panelItemGroups.new();

		// let groupNum = 0;
		let nPanelItemGroup = panelItemGroups.new();
		let prevSkuData: Component | null = null;

		// populate the first group with defaults
		// panelItemGroups.push(newPanelItemGroup());
		console.log('schema:groupRawAppliances:appliances', appliances);
		appliances?.forEach((appliance: RawAppliance, index) => {
			const skuData = this.#components.findSku(appliance.sku);
			console.log('schema:groupRawAppliances:appliance', index, appliance.sku, appliance);
			if (skuData && !skuData.sku.startsWith('NO-APPLIANCE')) {
				const nPanelItem = new PanelItem({
					dataIn: {
						item: {
							id: skuData.id,
							// ...skuData,
							exclude: false
						}
					}
				});
				nPanelItem.center.x = appliance.xPosition;

				// check on bounding
				if (index === 0 || prevSkuData === null) {
					nPanelItemGroup.items.new(nPanelItem.serialize());
				} else {
					// Match if the absolute value of xPosition delta is less than or equal to the absolute value cutout width delta where delta in appliance - prevAppliance
					const skuWidthDeltaHalf = Math.abs(skuData.cutout.width - prevSkuData.cutout.width) / 2;
					const skuDeltaX = Math.abs(appliance.xPosition - appliances[index - 1].xPosition);
					const xPositionMatch = skuDeltaX <= skuWidthDeltaHalf;

					if (xPositionMatch) {
						nPanelItemGroup.items.new(nPanelItem.serialize());
					} else {
						// groupNum += 1;
						nPanelItemGroup = panelItemGroups.new();
						nPanelItemGroup.items.new(nPanelItem.serialize());
					}
				}

				prevSkuData = skuData as Component;
			}
		});

		// Second pass - swap all items to trigger rules with complete groups
		panelItemGroups.children.forEach((group) => {
			group.items.children.forEach((item) => {
				// Create new PanelItem
				const newItem = new PanelItem({
					parent: group,
					sku: item.item.component.sku,
					dataIn: item.serialize()
				});
				// Replace old item with new one
				const index = group.items.children.indexOf(item);
				group.items.children[index] = newItem;
			});
		});

		panelItemGroups.purgeEmpty();

		// reset panel Item x
		panelItemGroups.children.forEach((group) => {
			group.items.children.forEach((item) => {
				item.center.x = 0;
				item.center.y = 0;
				item.center.z = 0;
			});
			group.centerItemsVertically();
		});

		frontPanel.distributeApplianceGroupsHorizontally();
		// console.log('schema:groupRawAppliances (groups)', applianceGrouping);

		// setKitchenConnections(this.#kitchen.design.islands);
	}


	// ---------------------------------------------
	// Private Methods - Processing Blueprint Data
	// ---------------------------------------------


	// 	// **************************
	// 	// -- Helper Functions
	// 	// **************************
	private getFuelchoice (raw: string): string {
		switch (raw) {
			case 'Natural Gas (NG)':
				return 'ng';
			case 'Liquid Propane (LP)':
				return 'lp-tank';
			default:
				return 'ng';
		}
	}
}
